import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Button, Radio, Switch } from 'antd'

import { ArrowRightOutlined } from '@ant-design/icons'
import { intersection } from 'lodash'

import { selectInterpolationData } from '@store/slices/shapes/selectors'

import { useLocalStorage } from '@/hooks'

import DataTypeSelect from './DataTypeSelect'

const pluralize = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`

const InterpolationWrapper = ({
  children,
  hasData,
  handleSelectData,
  rowMetaTags,
  dataKey,
  selectionsFor,
  setSelections,
  tableId,
  storedStrategy,
  rowNames,
  columnNames
}) => {
  const { dataSelection } = useSelector(state => selectInterpolationData(state, dataKey))
  const uniqueRowTags: string[] = useMemo(
    () => Array.from(new Set(rowMetaTags || [])),
    [rowMetaTags]
  )
  const [dynamicMappingEnabled, setDynamicMappingEnabled] = useState(false)
  const [sourceType, setSourceType] = useLocalStorage<string>('interpolationSourceType', 'data')
  const [activeRowTags, setActiveRowTags] = useLocalStorage<string[]>('interpolationRowTags', [])

  useEffect(() => {
    if (uniqueRowTags.length > 0 && intersection(uniqueRowTags, activeRowTags).length === 0) {
      setActiveRowTags([])
    }

    if (dataSelection?.rowTags) {
      setActiveRowTags(dataSelection.rowTags)
    } else if (activeRowTags.length === 0) {
      setActiveRowTags(uniqueRowTags)
    }

    if (dataSelection?.selectionStrategy && !storedStrategy) {
      if (['column', 'row'].includes(dataSelection.selectionStrategy)) {
        setDynamicMappingEnabled(true)
        setSourceType(dataSelection.selectionStrategy)
      } else {
        setDynamicMappingEnabled(false)
        setSourceType(
          {
            data: 'data',
            meta: 'meta',
            col_name: 'columnName',
            row_name: 'rowName'
          }[dataSelection.sourceType]
        )
      }
    }
  }, [dataSelection, uniqueRowTags])

  useEffect(() => {
    if (storedStrategy) {
      setDynamicMappingEnabled(['column', 'row'].includes(storedStrategy))
      setSourceType(storedStrategy)
    }
  }, [tableId, storedStrategy])

  const onDynamicEnable = (enabled: boolean) => {
    setDynamicMappingEnabled(enabled)
    if (enabled) {
      setSourceType('column')
    } else {
      setSourceType('data')
    }
  }
  const disabledRows = useMemo(() => {
    return (
      rowMetaTags
        ?.map((el, idx) => {
          return activeRowTags.includes(el) ? null : idx
        })
        ?.filter(el => el !== null) || []
    )
  }, [activeRowTags, rowMetaTags])

  const selections = selectionsFor(sourceType)
  const [[selectedRow] = [], [selectedColumn] = []] = selections
  const hasSelection = selectedRow != null && selectedColumn != null
  const selectBtnDisabled = !hasData || !hasSelection
  const selectionLabel =
    {
      data: [rowNames[selectedRow], columnNames[selectedColumn]].filter(Boolean).join(' · '),
      column: columnNames[selectedColumn],
      row: rowNames[selectedRow]
    }[sourceType] || 'Cell selected'

  const childrenProps = {
    disabledRows,
    selectionStrategy: sourceType,
    selections,
    sendSelections: value => setSelections(value, sourceType)
  }

  const handleOnSelect = () => {
    // here sourceType and selectionStrategy are merged, so we need to
    // distinguish them when API call is made
    const sourceTypeOptions = {
      data: 'data',
      meta: 'meta',
      rowName: 'row_name',
      columnName: 'col_name',
      column: 'data',
      row: 'data'
    }
    let selectionStrategy = 'cell'
    if (sourceType === 'column') {
      selectionStrategy = 'column'
    }
    if (sourceType === 'row') {
      selectionStrategy = 'row'
    }

    handleSelectData({
      selectionStrategy,
      sourceType: sourceTypeOptions[sourceType],
      rowTags: activeRowTags,
      selections
    })
  }

  return (
    <section className="workbench-card input-card">
      <div className="card-head">
        <h3>Input table</h3>
        {hasData && (
          <span className="muted">
            {pluralize(rowNames.length, 'row')} · {pluralize(columnNames.length, 'column')}
          </span>
        )}
      </div>

      {hasData && (
        <div className="card-toolbar">
          <div className="toolbar-field select-by">
            <span className="toolbar-label">Select by</span>
            <Radio.Group
              aria-label="Select by"
              className="segmented"
              optionType="button"
              value={sourceType}
              onChange={e => setSourceType(e.target.value)}
              options={
                dynamicMappingEnabled
                  ? [
                      { label: 'Columns', value: 'column' },
                      { label: 'Rows', value: 'row' }
                    ]
                  : [
                      { label: 'Table Values', value: 'data' },
                      { label: 'Table Details', value: 'meta' },
                      { label: 'Row Names', value: 'rowName' },
                      { label: 'Column Names', value: 'columnName' }
                    ]
              }
            />
          </div>
          <label className="toolbar-field switch-field">
            <Switch size="small" checked={dynamicMappingEnabled} onChange={onDynamicEnable} />
            Dynamic mapping
          </label>
          {uniqueRowTags.length > 0 && (
            <div className="toolbar-field data-type">
              <span className="toolbar-label">Data type</span>
              <DataTypeSelect
                rowMetaTags={rowMetaTags}
                value={activeRowTags}
                onChange={setActiveRowTags}
              />
            </div>
          )}
        </div>
      )}

      <div className="grid-area">
        {hasData ? (
          children(childrenProps)
        ) : (
          <div className="empty-state">Pick a table from the list on the left.</div>
        )}
      </div>

      <div className="card-foot">
        {hasData ? (
          <span className="selection-summary">
            {hasSelection ? (
              <>
                <span className="dot" />
                <span className="summary-text">{selectionLabel}</span>
              </>
            ) : (
              'Nothing selected yet'
            )}
          </span>
        ) : (
          <span />
        )}
        <Button disabled={selectBtnDisabled} onClick={handleOnSelect}>
          Select
          <ArrowRightOutlined />
        </Button>
      </div>
    </section>
  )
}

export default InterpolationWrapper
